import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import UserContext from '../context/UserContext'
import { LETTER_STATS_QUERY, RECORD_LETTER_MUTATION } from '../query/gqlQueries'

// Which letters of an alphabet this learner gets right and wrong. Every answer
// in the alphabet drills is recorded here: to the server per user
// ((User)-[:PRACTICED_LETTER]->(Letter)), and to a local copy so it works
// offline and before the backend has the query. The board colours letters by
// it and the tests draw harder on the weak ones — the learning is steered
// without anyone having to say so.
//
// One store per (user, language), shared by every component that asks: the
// test records an answer and the board, on another tab, sees it at once.

const keyFor = (username, lang) => `letterStats:${username || 'anon'}:${lang}`
const readLocal = (k) => { try { return JSON.parse(localStorage.getItem(k) || '{}') || {} } catch { return {} } }
const writeLocal = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch { /* private mode */ } }

const store = new Map()      // key → { [letter]: { right, wrong, streak, lastAt } }
const listeners = new Map()  // key → Set<fn>
const getStore = (k) => { if (!store.has(k)) store.set(k, readLocal(k)); return store.get(k) }
const setStore = (k, next) => { store.set(k, next); writeLocal(k, next); (listeners.get(k) || []).forEach(fn => fn(next)) }
const subscribe = (k, fn) => {
  if (!listeners.has(k)) listeners.set(k, new Set())
  listeners.get(k).add(fn)
  return () => { listeners.get(k).delete(fn) }
}

// new: never answered · weak: keeps going wrong · shaky: mixed or too few to
// tell · solid: reliably right.
export const levelOf = (s) => {
  if (!s) return 'new'
  const n = (s.right || 0) + (s.wrong || 0)
  if (n === 0) return 'new'
  const acc = (s.right || 0) / n
  const lastWrong = (s.streak || 0) === 0 && (s.wrong || 0) > 0
  if (acc < 0.6) return 'weak'
  if (lastWrong && acc < 0.75) return 'weak'          // going wrong, and just went wrong again
  if (acc >= 0.8 && n >= 3 && !lastWrong) return 'solid' // reliably right, and right last time
  return 'shaky'                                      // mixed, too few to tell, or one miss after a good run
}
// How much a letter wants practice, 0..~1.25: a smoothed error rate, plus a
// nudge when the last answer was wrong. Unknown letters sit in the middle.
export const troubleOf = (s) => {
  if (!s) return 0.5
  const n = (s.right || 0) + (s.wrong || 0)
  return ((s.wrong || 0) + 1) / (n + 2) + ((s.streak || 0) === 0 && (s.wrong || 0) > 0 ? 0.25 : 0)
}

export default function useLetterStats(lang) {
  const { username } = useContext(UserContext)
  const k = keyFor(username, lang)
  const [stats, setStats] = useState(() => getStore(k))
  // Follow the shared store: pick up the current value for this key and every
  // change to it, from any component.
  useEffect(() => { setStats(getStore(k)); return subscribe(k, setStats) }, [k])

  const { data } = useQuery(LETTER_STATS_QUERY, { variables: { username, lang }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [recordLetter] = useMutation(RECORD_LETTER_MUTATION)

  // Merge the server's view in. Where both know a letter, the one with more
  // answers wins — a failed mutation leaves the local copy ahead, never behind.
  useEffect(() => {
    const rows = data?.letterStats
    if (!rows) return
    const prev = getStore(k)
    const next = { ...prev }
    let changed = false
    rows.forEach(r => {
      const srv = { right: r.right || 0, wrong: r.wrong || 0, streak: r.streak || 0, lastAt: r.lastAt || null }
      const loc = prev[r.letter]
      const pick = loc && (loc.right + loc.wrong) > (srv.right + srv.wrong) ? loc : srv
      if (pick !== loc) { next[r.letter] = pick; changed = true }
    })
    if (changed) setStore(k, next)
  }, [data, k])

  const record = useCallback((letter, correct) => {
    if (!letter) return
    const prev = getStore(k)
    const s = prev[letter] || { right: 0, wrong: 0, streak: 0 }
    setStore(k, { ...prev, [letter]: { right: s.right + (correct ? 1 : 0), wrong: s.wrong + (correct ? 0 : 1), streak: correct ? (s.streak || 0) + 1 : 0, lastAt: new Date().toISOString() } })
    if (username) recordLetter({ variables: { username, lang, letter, correct: !!correct } }).catch(() => { /* kept locally */ })
  }, [username, lang, k, recordLetter])

  const weak = useMemo(() => Object.keys(stats).filter(l => levelOf(stats[l]) === 'weak'), [stats])
  const answered = useMemo(() => Object.values(stats).reduce((a, s) => a + (s.right || 0) + (s.wrong || 0), 0), [stats])
  return { stats, record, weak, answered, levelOf, troubleOf }
}
